import { effect, Injectable, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import EventEmitter from 'eventemitter2';
import { NotificationService } from '@app/notification/notification.service';
import { CaseOpenService } from '@features/case-open/case-open.service';
import { CaseBattle, CaseBattleHistoryEntry, CaseBattlePlayer, CaseBattleResult } from '@models/case-battle.interface';
import { Case } from '@models/case-open.interface';
import { SessionService } from '@shared/session.service';
import { SocketService } from '@shared/socket.service';
import { sleep } from '@utils';

@Injectable({
  providedIn: 'root',
})
export class CaseBattleService {
  currentBattle = signal<CaseBattle | null>(null);
  battles = signal<CaseBattle[]>([]);
  otherBattles = signal<CaseBattle[]>([]);
  myBattles = signal<CaseBattle[]>([]);

  events: EventEmitter = new EventEmitter();
  totalValue = signal(0);
  showOnlyJoinable = signal(false);
  showOnlyPrivate = signal(false);
  joinableCount = 0;
  sortBy: 'date' | 'price' = 'price';
  sortBySignal = signal<'date' | 'price'>('price');

  battleColors = ['', '#FF7A00', '#FF00B8', '#FFC700', '#00FFB2', '#FF4C41', '#4200FF', '#ADFF00', '#8F00FF', '#00FF38', '#00A3FF'];

  battleListening = 0;

  createBattleOptions: {
    crazyMode: boolean;
    private: boolean;
    fast: boolean;
    players: number;
    type: 'ffa' | 'team';
    nuclearMode: boolean;
    equityMode: boolean;
    cases: Case[];
    cover?: number;
    excluded?: boolean;
    depofilter?: number;
    affilter?: string;
  } = {
    crazyMode: false,
    private: false,
    fast: false,
    players: 2,
    type: 'ffa',
    nuclearMode: false,
    equityMode: false,
    cases: [],
    cover: 0,
    excluded: false,
    depofilter: 0,
    affilter: '0d',
  };

  readonly BATTLE_SETTINGS = {
    fast: {
      ROLL_TRANSITION_DURATION: 2000,
      ROLL_CLOSER_TRANSITION_DURATION: 100,
      ROLL_END_RUNNING_DELAY: 150,
      TIE_ROLL_TRANSITION_DURATION: 6000,
      TIE_ROLL_CLOSER_TRANSITION_DURATION: 300,
      TIE_ROLL_END_RUNNING_DELAY: 2000,
    },
    regular: {
      ROLL_TRANSITION_DURATION: 6500,
      ROLL_CLOSER_TRANSITION_DURATION: 300,
      ROLL_END_RUNNING_DELAY: 500,
      TIE_ROLL_TRANSITION_DURATION: 6000,
      TIE_ROLL_CLOSER_TRANSITION_DURATION: 300,
      TIE_ROLL_END_RUNNING_DELAY: 2000,
    },
  };

  constructor(
    private socketService: SocketService,
    private session: SessionService,
    private caseOpenService: CaseOpenService,
    private notificationsService: NotificationService,
    private router: Router,
  ) {
    effect(() => {
      const showOnlyJoinable = this.showOnlyJoinable();
      untracked(() => {
        this.fixList(showOnlyJoinable);
      });
    });

    effect(() => {
      this.showOnlyPrivate();
      untracked(() => {
        this.fixList(this.showOnlyJoinable());
      });
    });

    // `open_battles` is a room join the server drops on disconnect, and it is separate from
    // `battle_subscribe` — so it has to be re-sent on every reconnect, gated on the page refcount
    // so the room is only rejoined while the app still wants the list.
    effect(() => {
      if (this.socketService.connected() && this.battleListening > 0) {
        // Fire-and-forget, as in tf2-hunt: this is a room join, not a query. Awaiting an ack
        // spent a rate-limit slot and could time out on a join that actually succeeded.
        this.socketService.socket.emit('open_battles');
      }
    });

    this.caseOpenService.loadCases();
    this.socketService.socket.on('battle_list', (data: CaseBattle[]) => {
      this.battles.set([]);
      this.updateBattle(data);
    });

    this.socketService.socket.on('new_case_battle', async (data: CaseBattle[]) => {
      await this.updateBattle(data);
    });

    this.socketService.socket.on('battle_start', async (data: { uid: string }) => {
      const battle = this.battles().find((b) => b.uid === data.uid);
      if (!battle) {
        return;
      }

      battle.status = 'running';
      await this.updateBattle(battle);
    });

    this.socketService.socket.on('battle_seeds', async (data: { uid: string; server_seed: string; random_seed: string }) => {
      const battle = this.battles().find((b) => b.uid === data.uid);
      if (!battle) {
        return;
      }

      battle.server_seed = data.server_seed;
      battle.random_seed = data.random_seed;
      await this.updateBattle(battle);
    });

    this.socketService.socket.on('battle_remove', async (data: { uid: string }) => {
      const battle = this.battles().find((b) => b.uid === data.uid);
      this.battles.set(this.battles().filter((b) => b.uid !== data.uid));
      if (battle?.status === 'open' && this.currentBattle()?.uid === data.uid) {
        this.currentBattle.set(null);
        this.router.navigate(['/battles']);
        this.notificationsService.info('Case battle has been cancelled.', 'Case Battle');
      }
      this.fixList();
    });

    this.socketService.socket.on('battle_end', async (data: { results: CaseBattleResult[]; roundNumber: number; uid: string }) => {
      const battle = this.battles().find((b) => b.uid === data.uid);
      if (!battle || battle.currentRoundNumber > data.roundNumber) {
        return;
      }

      if (battle.status === 'open') {
        battle.status = 'running';
      }

      battle.currentRoundNumber = data.roundNumber;
      const c = await this.getFullCase(battle.cases[battle.currentRoundNumber - 1]);
      for (const res of data.results) {
        // The case list can be stale relative to the server, so a missing case must not throw here —
        // that would skip `updateBattle` below and freeze the battle page on this round.
        res.item = c?.skins?.find((s) => s.name === res.skin);
      }

      battle.currentRound = {
        roundNumber: data.roundNumber,
        drops: data.results,
        case: c,
      };

      battle.rounds.push({
        roundNumber: data.roundNumber,
        drops: data.results,
      });

      if (!this.currentBattle() || this.currentBattle().uid !== battle.uid) {
        await this.appendResultsToBattle(battle, data.results);
      }

      await this.updateBattle(battle);
      if (this.currentBattle() && this.currentBattle().uid === battle.uid) {
        this.events.emit('battle_end', data);
      }
    });

    this.socketService.socket.on('battle_ended', async (data: CaseBattle) => {
      if (data.tie) {
        data.tie_running = true;
      }
      await this.updateBattle(data);
      if (data.tie) {
        if (this.currentBattle() && this.currentBattle().uid === data.uid) {
          this.events.emit('battle_tie', data);
        }
        await sleep(8000);
        data.tie_running = false;
        await this.updateBattle(data);
      }

      if (this.session.profile() && data.winners.some((w) => w.steamid === this.session.profile().steamid)) {
        void this.session.updateBalance().catch((): void => undefined);
      }
    });

    this.socketService.socket.on('battle_add_user', async (data: { uid: string; user: CaseBattlePlayer }) => {
      const battle = this.battles().find((b) => b.uid === data.uid);
      if (battle) {
        const index = battle.participants.findIndex((p) => p.slot === data.user.slot);
        battle.participants[index] = data.user;

        if (battle.participants.filter((i) => i.id !== -1).length === battle.max_participants) {
          battle.status = 'running';
        }
      }
      await this.updateBattle(battle);
    });
  }

  async updateBattle(battles: CaseBattle | CaseBattle[], getCurr = false) {
    if (!Array.isArray(battles)) {
      battles = [battles];
    }

    for (const battle of battles) {
      const formattedBattle = await this.formatBattle(battle, getCurr);

      const arr = this.battles();
      const idx = arr.findIndex((b) => b.uid === formattedBattle.uid);

      if (idx !== -1) {
        const newArr = [...arr];
        newArr[idx] = { ...arr[idx], ...formattedBattle };
        this.battles.set(newArr);
      } else {
        this.battles.set([...arr, formattedBattle]);
      }

      if (this.currentBattle()?.uid === formattedBattle.uid) {
        this.currentBattle.set(formattedBattle);
      }
    }

    this.fixList();
  }

  async appendResultsToBattle(battle: CaseBattle, results: CaseBattleResult[]) {
    const c = await this.getFullCase(results[0].case);
    for (const res of results) {
      const item = c.skins.find((s) => s.name === res.skin);
      const part = battle.participants.find((p) => p.slot === res.user.slot);
      if (part) {
        if (!part.drops) {
          part.drops = [];
        }

        part.drops.push({
          ...item,
          case: c,
          ticket: res.multiplier,
        });
        part.value = part.drops.reduce((a, b) => a + b.price, 0);

        if (battle.mode == 'nuclear' || battle.mode === 'crazynuclear') {
          part.value = 0;
          if (battle.currentRoundNumber == battle.roundsNumber || battle.status === 'finished') {
            part.value = part.drops[part.drops.length - 1].price;
          }
        }
      }
    }

    if (battle.type === 'team') {
      const team1 = battle.participants.filter((p) => p.slot <= battle.max_participants / 2);
      const team2 = battle.participants.filter((p) => p.slot > battle.max_participants / 2);
      const totalValueTeam1 = team1.reduce((a, b) => a + b.value, 0);
      const totalValueTeam2 = team2.reduce((a, b) => a + b.value, 0);

      if (battle.mode !== 'crazy' && battle.mode !== 'crazynuclear') {
        for (const user of team1) {
          user.is_leading = totalValueTeam1 >= totalValueTeam2 ? true : false;
        }

        for (const user of team2) {
          user.is_leading = totalValueTeam2 >= totalValueTeam1 ? true : false;
        }
      } else {
        for (const user of team1) {
          user.is_leading = totalValueTeam2 >= totalValueTeam1 ? true : false;
        }

        for (const user of team2) {
          user.is_leading = totalValueTeam1 >= totalValueTeam2 ? true : false;
        }
      }
    } else {
      if (battle.mode === 'equity') {
        for (const user of battle.participants) {
          user.is_leading = true;
        }
      } else if (battle.mode === 'crazy' || battle.mode === 'crazynuclear') {
        let lowestValue = Number.MAX_VALUE;
        let lowestValueUsers: CaseBattlePlayer[] = [];
        for (const user of battle.participants) {
          user.is_leading = false;
          if (user.value < lowestValue) {
            lowestValue = user.value;
            lowestValueUsers = [user];
          } else if (user.value === lowestValue) {
            lowestValueUsers.push(user);
          }
        }

        for (const user of lowestValueUsers) {
          user.is_leading = true;
        }
      } else {
        let highestValue = 0;
        let highestValueUsers: CaseBattlePlayer[] = [];
        for (const user of battle.participants) {
          user.is_leading = false;
          if (user.value > highestValue) {
            highestValue = user.value;
            highestValueUsers = [user];
          } else if (user.value === highestValue) {
            highestValueUsers.push(user);
          }
        }

        for (const user of highestValueUsers) {
          user.is_leading = true;
        }
      }
    }
  }

  async formatBattle(battleParam: CaseBattle, getCurr = false) {
    const battle = { ...battleParam };

    battle.roundsArray = [];
    battle.nextCases = [];
    for (let i = 0; i < battle.roundsNumber; i++) {
      battle.roundsArray.push(i);
    }

    for (const c of battle.cases) {
      if (!c.color) {
        if (c.price > 125) {
          c.color = '255, 188, 57';
        } else if (c.price > 50) {
          c.color = '213, 63, 17';
        } else if (c.price > 5) {
          c.color = '68, 17, 213';
        } else {
          c.color = '116, 192, 217';
        }
      }
    }

    for (const p of battle.participants) {
      if ((p as any).user) {
        p.avatar = (p as any).user.avatar;
        p.name = (p as any).user.name;
        p.steamid = (p as any).user.steamid;
        p.level = (p as any).user.level;
      }

      p.team = p.slot;

      if (battle.type === 'team') {
        p.team = p.slot > 2 ? 2 : 1;
      }
    }

    if (battle.status === 'open') {
      battle.rounds = [];
      battle.currentRoundNumber = 1;
      battle.overallPot = 0;

      for (let i = 1; i <= battle.max_participants; i++) {
        const part = battle.participants.find((p) => p.slot === i);
        if (!part) {
          battle.participants.push({
            slot: i,
            value: 0,
            id: -1,
            steamid: '',
            name: '',
            avatar: '',
            level: 0,
            drops: [],
            isEmptySlot: true,
          });
        } else {
          part.drops = [];
          part.value = 0;
        }
      }
      battle.participants = battle.participants.sort((a, b) => a.slot - b.slot);
    }

    if (battle.status === 'running' || battle.status === 'finished') {
      battle.currentRoundNumber = battle?.currentRoundNumber ?? battle.rounds.length ?? 0;

      const rounds = battle.status === 'running' ? battle.rounds.slice(0, getCurr ? battle.currentRoundNumber : battle.currentRoundNumber - 1) : battle.rounds;

      battle.overallPot = rounds.reduce((acc, round) => acc + round.drops.reduce((acc2, drop) => acc2 + drop.price, 0), 0);

      for (const p of battle.participants) {
        p.drops = [];

        if (battle.type === 'team') {
          p.teammate = battle.participants.find((i) => p.slot !== i.slot && (p.slot === 2 || p.slot === 1 ? i.slot === 1 || i.slot === 2 : i.slot === 3 || i.slot === 4)) ?? null;
        }
      }

      for (const round of rounds) {
        await this.appendResultsToBattle(battle, round.drops);
      }

      for (const p of battle.participants) {
        p.value = p.drops.reduce((acc, drop) => acc + drop.price, 0);
        if (battle.status === 'finished') {
          p.is_winner = battle.winners.find((i) => i.slot === p.slot) ? true : false;
          p.winValue = p.is_winner ? battle.overallPot / battle.winners.length : 0;
        }

        if (battle.mode == 'nuclear' || battle.mode === 'crazynuclear') {
          p.value = 0;
          if (battle.currentRoundNumber == battle.roundsNumber || battle.status === 'finished') {
            p.value = p.drops?.[p.drops.length - 1]?.price ?? 0;
          }
        }
      }
    }

    if (battle.status === 'finished') {
      battle.currentRoundNumber = battle.rounds.length;

      for (const winner of battle.winners) {
        winner.winValue = battle.overallPot / battle.winners.length;
        winner.is_winner = true;
      }

      if (battle.tie) {
        if ((battle as any).tiebreaker) {
          battle.tie_array = (battle as any).tiebreaker;
        }
        if (battle.type === 'team' && battle.tie_array.length !== 4) {
          const slot1 = battle.tie_array.find((i) => i.slot === 1);
          const slot2 = battle.tie_array.find((i) => i.slot === 2);
          battle.tie_array = [slot1, { ...slot1, slot: 2 }, { ...slot2, slot: 3 }, { ...slot2, slot: 4 }];
        }

        for (const tieItem of battle.tie_array) {
          const part = battle.participants.find((p) => p.slot === tieItem.slot);
          part.is_tie = true;
          part.tie_chance = tieItem.chance;
        }
      }
    }

    battle.participants = battle.participants.sort((a, b) => a.slot - b.slot);

    return battle;
  }

  fixList(onlyJoinable = false) {
    const sortedList = this.battles().sort(this.sortBy === 'date' ? (a, b) => b.created - a.created : (a, b) => b.value - a.value);
    const myBattlesArray: CaseBattle[] = [];
    const otherBattlesArray: CaseBattle[] = [];

    const showOnlyPrivate = this.showOnlyPrivate();

    const filteredBattles = sortedList.filter((c) => {
      const isUserInvolved = this.session?.profile() && (c.participants.some((p) => p.steamid === this.session.profile().steamid) || c.host === this.session.profile().steamid);

      if (isUserInvolved) {
        return true;
      }

      if (c.private && !showOnlyPrivate) {
        return false;
      }

      if (showOnlyPrivate && !c.private) {
        return false;
      }

      return true;
    });

    filteredBattles.forEach((b) => {
      const userSteamID = this.session.profile()?.steamid;

      const { status, participants, host } = b;

      const isBattleOpen = status === 'open';

      const isUserAlreadyJoined = participants?.some((u) => u.steamid === userSteamID);
      const isUserHost = host === userSteamID;

      const isMyBattle = isUserAlreadyJoined || isUserHost;

      if (onlyJoinable && (!isBattleOpen || isUserAlreadyJoined)) {
        return;
      }

      if (isMyBattle) {
        myBattlesArray.push(b);
      } else {
        otherBattlesArray.push(b);
      }
    });

    this.myBattles.set(myBattlesArray);
    this.otherBattles.set(otherBattlesArray);

    this.totalValue.set(filteredBattles.reduce((acc, c) => acc + c.value, 0));
    this.joinableCount = filteredBattles.filter((c) => c.status === 'open').length;
  }

  toggleFilterGamesList() {
    this.showOnlyJoinable.update((value) => !value);
  }

  togglePrivateGamesList() {
    this.showOnlyPrivate.update((value) => !value);
  }

  async getFullCase(c: Case | string): Promise<Case> {
    if (!c) {
      return null;
    }

    if ((c as Case)?.skins) {
      return c as Case;
    }

    // Battle payloads may carry short cases (link/price only), so the full case is resolved from the
    // list — which has to be awaited here, or a battle that arrives before the list resolves to nothing.
    const cases = await this.caseOpenService.loadCases();
    const link = typeof c === 'string' ? c : c.link;

    return cases.find((i) => i.link === link);
  }

  async getBattle(uid: string) {
    try {
      const result = await this.socketService.emit<CaseBattle>('get_battle', { uid }, { noErrorMessage: true, timeout: 10000 });
      const formattedBattle = await this.formatBattle(result);
      this.currentBattle.set(formattedBattle);

      // check if battle in the battles list if not add it
      if (this.currentBattle().status !== 'finished') {
        const existing = this.battles().find((b) => b.uid === this.currentBattle().uid);
        if (!existing) {
          this.battles.update((arr) => [...arr, this.currentBattle()]);
        }
      }
    } catch (err) {
      console.log(err);
    }
    return this.currentBattle();
  }

  /** Fire-and-forget: the backend gates a battle's round events on this subscription. Re-sent on reconnect. */
  subscribeBattle(uid: string) {
    this.socketService.socket.emit('battle_subscribe', { uid });
  }

  async joinBattle(uid: string, slot: number = undefined) {
    await this.socketService.emit<boolean>('join_battle', { uid, slot, balance: this.session.balanceType() });
    this.notificationsService.success('You have joined the case battle.', 'Case Battle', { bet: false });
  }

  async cancelBattle(battle: CaseBattle) {
    await this.socketService.emit<boolean>('battle_cancel', { uid: battle.uid });
    this.router.navigate(['/battles']);
    this.notificationsService.info('Case battle has been cancelled.', 'Case Battle');
    void this.session.updateBalance().catch((): void => undefined);
  }

  async callBots(uid: string) {
    await this.socketService.emit<boolean>('bot_battle', { uid });
    this.notificationsService.success('Bots will join your case battle shortly.', 'Case Battle');
  }

  async createBattle(options: {
    fast: boolean;
    mode: string;
    type: string;
    participants: number;
    cases: Case[];
    private: boolean;
    excluded: boolean;
    cover: number;
    depofilter: number;
    affilter: string;
  }) {
    const overallValue = options.cases.reduce((a, b) => a + b.price * b.amount, 0);

    if (this.session.currentBalance() < overallValue) {
      this.notificationsService.error('You do not have enough balance to create this battle.');
      return null;
    }

    // make sure its one of this modes normal, crazy, nuclear
    if (!['normal', 'crazy', 'nuclear', 'crazynuclear', 'equity'].includes(options.mode)) {
      this.notificationsService.error('Invalid mode.');
      return null;
    }

    // make sure its type ffa or team
    if (!['ffa', 'team'].includes(options.type)) {
      this.notificationsService.error('Invalid type.');
      return null;
    }

    const casesArray = options.cases.map((c) => Array(c.amount).fill(c.link)).flat();

    let res: CaseBattle;
    try {
      res = await this.socketService.emit<CaseBattle>('host_battle', {
        mode: options.mode,
        type: options.type,
        participants: options.participants,
        cases: casesArray,
        fast: options.fast,
        private: options.private,
        excluded: options.cover >= 80 && options.private ? options.excluded : false,
        cover: options.private ? options.cover : 0,
        depofilter: options.private ? options.depofilter : 0,
        affilter: options.private ? options.affilter : '0d',
        balance: this.session.balanceType(),
      });
    } catch {
      // The socket layer already toasted; callers only need a falsy result so they can close/reset.
      return null;
    }

    // `formatBattle` iterates `cases`, so an ack that is not a battle would throw instead of returning.
    if (!res?.cases) {
      return null;
    }

    this.createBattleOptions = {
      crazyMode: false,
      private: false,
      fast: false,
      players: 2,
      type: 'ffa',
      nuclearMode: false,
      equityMode: false,
      cases: [],
      cover: 0,
      excluded: false,
      depofilter: 0,
      affilter: '0d',
    };
    this.caseOpenService.cases.forEach((c) => (c.amount = 0));
    const formattedBattle = this.formatBattle(res);
    void this.session.updateBalance().catch((): void => undefined);
    return formattedBattle;
  }

  async openCaseBattles() {
    this.battleListening++;
    if (this.battleListening > 1) {
      return;
    }

    // Room joins/leaves have no user-facing failure mode, but the default emit timeout still
    // rejects — swallow it so it cannot surface as a toast or an unhandled rejection.
    await this.socketService.emitArgs('open_battles', undefined, { noErrorMessage: true }).catch((): void => undefined);
  }

  async closeCaseBattles() {
    this.battleListening--;
    if (this.battleListening > 0) return;

    setTimeout(async () => {
      if (this.battleListening > 0) return;

      await this.socketService.emitArgs('off_battles', undefined, { noErrorMessage: true }).catch((): void => undefined);
    }, 1000);
  }

  toggleSort(value?: 'date' | 'price') {
    if (value !== undefined) {
      this.sortBy = value;
    } else {
      this.sortBy = this.sortBy === 'date' ? 'price' : 'date';
    }

    this.sortBySignal.update((prev) => (prev === 'date' ? 'price' : 'date'));

    this.fixList();
  }

  /**
   * The backend serves only the caller's own battle history, and only as summary rows — there is no
   * all-players feed and no paging. Rows are widened back into `CaseBattle` so the existing
   * `app-case-battle-row` renders them unchanged.
   */
  async getMyHistory(): Promise<CaseBattle[]> {
    const rows = await this.socketService.emit<CaseBattleHistoryEntry[]>('get_my_case_battle_history', {}, { noError: true });
    if (!Array.isArray(rows)) {
      return [];
    }

    // Per-row isolation: one malformed entry must not blank the whole page.
    const battles = await Promise.all(
      rows.map(async (row) => {
        try {
          return await this.formatBattle(this.historyEntryToBattle(row));
        } catch (err) {
          console.warn('[CaseBattles] skipping unrenderable history entry', row?.uid, err);
          return null;
        }
      }),
    );

    return battles.filter((b) => b);
  }

  private historyEntryToBattle(entry: CaseBattleHistoryEntry): CaseBattle {
    const cases = (entry.cases ?? []).flatMap((c) => Array(Math.max(1, c.amount ?? 1)).fill(c)) as Case[];
    const winnerSlots = new Set((entry.winner ?? []).map((w) => w.slot));
    const participants = (entry.players ?? []) as unknown as CaseBattlePlayer[];

    return {
      ...(entry as unknown as CaseBattle),
      status: 'finished',
      cases,
      // `appendResultsToBattle` reads `drops[0].case`, so rounds that carry no drops are dropped here.
      rounds: [...(entry.drops ?? [])]
        .filter((r) => r.drops?.length)
        .sort((a, b) => a.round - b.round)
        .map((r) => ({ roundNumber: r.round, drops: r.drops })),
      currentRound: null,
      max_participants: entry.participants ?? participants.length,
      participants,
      winners: participants.filter((p) => winnerSlots.has(p.slot)),
      tie: (entry.tiebreaker ?? []).length > 0,
      created: entry.time,
    };
  }

  getBattleSettings(fast: CaseBattle['fast']) {
    return this.BATTLE_SETTINGS[fast ? 'fast' : 'regular'];
  }
}
